import { RouteObject } from 'react-router-dom'
import { lazy } from 'react'
import PrivateRoute from './PrivateRoute'
import Loadable from '../components/Loadable'
import Orders from '../views/Orders/OrdersTable'
import OrderPreview from '../views/OrderPreviews'
import Customers from '../views/Customers'
import ReadEditCustomer from '../views/Customers/CustomerItem'
import Leads from '../views/Leads/Campaigns'
import LeadsOverview from '../views/Leads/LeadsOverview'
import LeadsByOrder from '../views/Orders/OrderItem/LeadsByOrder'
import AccountsAndPermissions from '../views/AccountsAndPermissions'
import MainLayout from '../layout/MainLayout'
import Accounts from '../views/AccountsAndPermissions/Accounts'
import Jobs from '../views/Jobs'
import ProductManagement from '../views/ProductManagement'
import OrderItem from '../views/Orders/OrderItem'
import OrderRedirect from '../components/OrderRedirect'
import LegacyCampaignRedirect from '../components/LegacyCampaignRedirect'
import Page404 from '../views/404'
import AnalyticsDashboard from '../views/Analytics/components/Dashboard'
import PerformanceKPI from '../views/Analytics/components/AdsetsKPIs'
import CampaignList from '../views/Analytics/components/CampaignList'
import { PAGE } from '../constants'
import { Analytics, UserProfile } from '../views'
import { LeadsList } from '../views/Leads/LeadsList'

// sample page routing
const Dashboard = Loadable(lazy(() => import('../views/Dashboard')))

const children: RouteObject[] = [
  {
    path: '/',
    element: <Dashboard />,
  },
  {
    path: PAGE.DASHBOARD.PATH,
    element: <Dashboard />,
  },
  {
    path: PAGE.ORDERS.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDERS.ROLES}>
        <Orders />
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.ORDER.PATH}/:id`,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDER.ROLES}>
        <OrderRedirect>
          <OrderItem />
        </OrderRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.ORDER.PATH}/:id/leads`,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDER.ROLES}>
        <OrderRedirect>
          <OrderItem />
        </OrderRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.ORDER.PATH}/:id/analytics`,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDER.ROLES}>
        <OrderRedirect>
          <OrderItem />
        </OrderRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.ORDER.PATH}/:id/application`,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDER.ROLES}>
        <OrderRedirect>
          <OrderItem />
        </OrderRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.ORDER.PATH}/:id/social-ad`,
    element: (
      <PrivateRoute allowedRoles={['admin']}>
        <OrderRedirect>
          <OrderItem />
        </OrderRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.ORDER.PATH}/:id/meta-leadgen-form`,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDER.ROLES}>
        <OrderRedirect>
          <OrderItem />
        </OrderRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.ORDER.PATH}/:id/campaign-setup`,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDER.ROLES}>
        <OrderRedirect>
          <OrderItem />
        </OrderRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.JOBS.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.JOBS.ROLES}>
        <Jobs />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.PRODUCT_MANAGEMENT.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.PRODUCT_MANAGEMENT.ROLES}>
        <ProductManagement />
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.ORDERS.PATH}/:id`,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDERS.ROLES}>
        <OrderRedirect>
          <OrderItem />
        </OrderRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.ORDER_PREVIEWS.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.ORDER_PREVIEWS.ROLES}>
        <OrderPreview />
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.CAMPAIGNS.PATH}/:id`,
    element: (
      <PrivateRoute allowedRoles={PAGE.CAMPAIGNS.ROLES}>
        <LegacyCampaignRedirect>
          <LeadsList />
        </LegacyCampaignRedirect>
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.USER.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.USER.ROLES}>
        <UserProfile />
      </PrivateRoute>
    ),
    children: [
      { path: 'profile', element: <UserProfile /> },
      { path: 'security', element: <UserProfile /> },
      { path: 'integrations', element: <UserProfile /> },
      { path: 'businessInformation', element: <UserProfile /> },
    ],
  },
  {
    path: `${PAGE.CUSTOMERS.PATH}/:id`,
    element: (
      <PrivateRoute allowedRoles={PAGE.CUSTOMERS.ROLES}>
        <ReadEditCustomer />
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.CUSTOMERS.PATH}/:id/integrations`,
    element: (
      <PrivateRoute allowedRoles={PAGE.CUSTOMERS.ROLES}>
        <ReadEditCustomer />
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.CUSTOMERS.PATH}/:id/customer-permission`,
    element: (
      <PrivateRoute allowedRoles={PAGE.CUSTOMERS.ROLES}>
        <ReadEditCustomer />
      </PrivateRoute>
    ),
  },
  {
    path: `${PAGE.CUSTOMERS.PATH}/:id/email-settings`,
    element: (
      <PrivateRoute allowedRoles={PAGE.CUSTOMERS.ROLES}>
        <ReadEditCustomer />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.CUSTOMERS.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.CUSTOMERS.ROLES}>
        <Customers />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.ACCOUNTS_AND_PERMISSIONS.ACCOUNTS.INTERNAL.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.ACCOUNTS_AND_PERMISSIONS.ROLES}>
        <Accounts />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.ACCOUNTS_AND_PERMISSIONS.ACCOUNTS.EXTENRAL.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.ACCOUNTS_AND_PERMISSIONS.ROLES}>
        <Accounts />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.ACCOUNTS_AND_PERMISSIONS.PERMISSIONS.RESELLER.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.ACCOUNTS_AND_PERMISSIONS.ROLES}>
        <AccountsAndPermissions />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.ACCOUNTS_AND_PERMISSIONS.PERMISSIONS.RESELLER_CUSTOMER.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.ACCOUNTS_AND_PERMISSIONS.ROLES}>
        <AccountsAndPermissions />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.ACCOUNTS_AND_PERMISSIONS.PERMISSIONS.DIRECT_CUSTOMER.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.ACCOUNTS_AND_PERMISSIONS.ROLES}>
        <AccountsAndPermissions />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.LEADS.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.LEADS.ROLES}>
        <Leads />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.LEADS_OVERVIEW.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.LEADS_OVERVIEW.ROLES}>
        <LeadsOverview />
      </PrivateRoute>
    ),
  },
  {
    path: '/leads-by-order/:orderId',
    element: (
      <PrivateRoute allowedRoles={PAGE.LEADS_BY_ORDER.ROLES}>
        <LeadsByOrder />
      </PrivateRoute>
    ),
  },
  {
    path: PAGE.ANALYTICS.PATH,
    element: (
      <PrivateRoute allowedRoles={PAGE.ANALYTICS.ROLES}>
        <Analytics />
      </PrivateRoute>
    ),
    children: [
      { path: '', element: <CampaignList /> },
      { path: 'dashboard/:id', element: <AnalyticsDashboard /> },
      { path: 'performance/:id', element: <PerformanceKPI /> },
    ],
  },
  {
    path: '*',
    element: <Page404 />,
  },
]

const MainRoutes = {
  path: '/',
  element: <MainLayout />,
  children,
}

export default MainRoutes
